import React, { useEffect, useRef, useState } from "react";
import { DeleteIcon, EditIcon, ExportIcon, VerticalMoreBlueIcon } from "../../svg";
import IconBtn from "../../form/icon-btn";
import CardMoreOptions from "../../form/card-more-options";

interface RemoteMonitoringCardProps {
  item: any;
  isExternalUser: boolean;
  onEdit: (item: any) => void;
  onDelete: (item: any) => void;
  onDownload: (item: any) => void;
  onMenuClick?: (e: any) => void;
}

const RemoteMonitoringCard: React.FC<RemoteMonitoringCardProps> = ({
  item,
  isExternalUser,
  onEdit,
  onDelete,
  onDownload,
  onMenuClick
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Options for the dropdown
  const options = [
    //{ label: "Download", icon: <ExportIcon />, token: "web.common.button.download" },
    { label: "Edit", icon: <EditIcon />, token: "web.title.common.edit" },
    { label: "Delete", icon: <DeleteIcon />, token: "web.title.common.delete" },
  ];

  // Handle Menu Actions
  const handleOptionSelect = (option: any) => {
    setIsMenuOpen(false); // Close menu
    if (option.label === "Edit") onEdit(item);
    if (option.label === "Delete") onDelete(item);
    //if (option.label === "Download") onDownload(item);
  };

  const toggleMenu = (e: any) => {
    e.stopPropagation();
    if (onMenuClick) onMenuClick(e);
    setIsMenuOpen(!isMenuOpen);
  };

  // Click Outside Logic (Local to this specific card)
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="w-full h-full group cursor-pointer">
      <div className="w-full h-auto rounded-[0.738rem] bg-io-gray-fa dark:bg-io-black-1a group-hover:bg-io-white group-hover:dark:bg-io-black-29 group-hover:shadow-md transition-all duration-300 border border-transparent group-hover:border-gray-100">
        <div className="w-full h-full p-[1rem] relative">
          
          {/* Image Container */}
          <div className="w-full h-[20rem] mb-2 rounded-[0.5rem] relative overflow-hidden bg-gray-200">
            {item.url ? (
              <img
                src={item.url}
                alt={"Screenshot"}
                className="w-full h-full object-cover object-center"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-300 text-gray-500 font-bold uppercase">
                {"I"}
              </div>
            )}
          </div>

          {/* More Button & Menu (Independent for each card) */}
          {!isExternalUser && (
            <div 
              className={`w-auto h-auto absolute top-[10%] right-[5%] z-20 ${isMenuOpen ? 'block' : 'hidden group-hover:block'}`} 
              ref={menuRef}
            >
              <div className="relative">
                <IconBtn
                  size="w-[2rem]"
                  icon={<VerticalMoreBlueIcon />}
                  handleClick={toggleMenu}
                />
                
                {isMenuOpen && (
                  <div className="w-auto h-auto absolute top-full right-[-1rem] pt-2 animate-fade-in-down">
                    <CardMoreOptions
                      options={options}
                      onSelectOption={handleOptionSelect}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Content Section */}
          <div className="pl-[0.2rem]">
            <h4
              className="text-[1.32rem] font-inter font-semibold text-io-black dark:text-io-white truncate"
              title={item.destinationUserName || "Unknown User"}
            >
              {item.destinationUserName || "Unknown User"}
            </h4>

            {/* Description (Truncated) */}
            {item.description && (
              <p
                className="text-[1.2rem] font-inter font-normal text-io-gray-66 dark:text-io-black-d1 mt-1 truncate"
                title={item.description}
              >
                {item.description}
              </p>
            )}

            <div className="flex justify-between items-center mt-1 text-[1rem] text-io-gray-47">
              <p>{item.time}</p>
              <p>{item.date}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoteMonitoringCard;